import React, { useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { TextInputFormField } from '../../../Shared/TextInputFormField';
import SelectFormField from '../../../Shared/SelectFormField';
import MaskedInput from '../../../Shared/MaskedInput';
import { US_STATES } from '../../../../shared/constants';
import {
  convertHoursToSeconds,
  formatDateIgnoreTz,
  PHONE_REGEX,
} from '../../../../shared/utils';
import {
  updateFacility,
  updateParentFacility,
} from '../../../../store/facility/actionCreators';
import {
  Location,
  UpdateFacility,
  UpdateParentFacility,
} from '../../../../store/facility/facilityTypes';
import {
  dimGrey,
  lightBlack,
  selectFieldStyles,
  textFieldStyles,
} from '../../../../mainTheme';
import { TabProps } from '../FacilityDetails';

const MEDICATION_WINDOWS = [
  '1 hour',
  '2 hours',
  '3 hours',
  '4 hours',
  '5 hours',
  '6 hours',
  '7 hours',
  '8 hours',
];

const PHONE_FORMAT = '(###) ###-####';

const medWindowReadLabel = (sec?: number) => {
  if (!sec) return 'No medication window set';
  const hours = Math.floor(sec / 3600);
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
};

const medWindowOption = (sec?: number) => {
  if (!sec) return '';
  const label = medWindowReadLabel(sec);
  return MEDICATION_WINDOWS.includes(label) ? label : '';
};

// This is an edit form, so a field is only validated when the user actually
// changes it from its loaded value — leaving an already-blank field untouched
// must not block saving a different field. Once a field is edited, it has to
// meet the same rule the create forms enforce.
const requiredIfEdited = (initial: string, message: string) =>
  Yup.string().test(
    'required-if-edited',
    message,
    (value) => value === initial || !!(value && value.trim()),
  );

const phoneIfEdited = (initial: string) =>
  Yup.string().test(
    'phone-if-edited',
    'Phone number is not valid',
    (value) => value === initial || (!!value && PHONE_REGEX.test(value)),
  );

// The city/state/zip/phone rules are identical for the location and facility
// forms, so define them once here rather than in each schema.
const addressFieldValidators = (initial: {
  city: string;
  state: string;
  zip_code: string;
  phone: string;
}) => ({
  city: requiredIfEdited(initial.city, 'City is required'),
  state: requiredIfEdited(initial.state, 'State is required'),
  zip_code: requiredIfEdited(initial.zip_code, 'Zip code is required'),
  phone: phoneIfEdited(initial.phone),
});

const formActionButtonSx = {
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '14px',
  height: '40px',
  px: 2,
} as const;

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box mb={3}>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '22px',
        letterSpacing: '0.2px',
        color: lightBlack,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.2px',
        color: dimGrey,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const renderAddress = (
  line1?: string,
  line2?: string,
  city?: string,
  state?: string,
  zip?: string,
) => {
  const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return (
    <>
      {line1 || '—'}
      {line2 ? (
        <>
          <br />
          {line2}
        </>
      ) : null}
      {cityStateZip ? (
        <>
          <br />
          {cityStateZip}
        </>
      ) : null}
    </>
  );
};

const contentColumnSx = { maxWidth: '608px', flex: 1, width: '100%' } as const;

const DetailsTab = ({
  facility,
  locationId,
  isFacilityAdmin,
  onDeactivateFacility,
}: TabProps) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  // Match FacilityDetailsForm: don't surface errors until the first save attempt.
  const [validateOnChange, setValidateOnChange] = useState(false);

  const isLocation = !!locationId;

  const location: Location | undefined = useMemo(() => {
    if (!isLocation) return undefined;
    return facility?.locations?.find((loc: Location) => loc.id === locationId);
  }, [facility, locationId, isLocation]);

  const stateOptions = useMemo(
    () => US_STATES.map((s) => ({ label: s.name, value: s.id })),
    [],
  );

  const locationInitialValues = useMemo(
    () => ({
      med_window: medWindowOption(location?.med_window_time_sec),
      address: location?.address || '',
      city: location?.city || '',
      state: location?.state || '',
      zip_code: location?.zip_code || '',
      phone: location?.phone || '',
    }),
    [location],
  );

  const locationValidationSchema = Yup.object({
    med_window: requiredIfEdited(
      locationInitialValues.med_window,
      'Medication window is required',
    ),
    address: requiredIfEdited(
      locationInitialValues.address,
      'Address is required',
    ),
    ...addressFieldValidators(locationInitialValues),
  });

  const handleLocationSubmit = async (values: typeof locationInitialValues) => {
    const initial = locationInitialValues;
    const payload: UpdateFacility = {};
    if (values.address !== initial.address) payload.address = values.address;
    if (values.city !== initial.city) payload.city = values.city;
    if (values.state !== initial.state) payload.state = values.state;
    if (values.zip_code !== initial.zip_code)
      payload.zip_code = values.zip_code;
    if (values.phone !== initial.phone) payload.phone = values.phone;
    if (values.med_window !== initial.med_window)
      payload.med_window_time_sec = convertHoursToSeconds(values.med_window);

    if (Object.keys(payload).length === 0) {
      setIsEditing(false);
      return;
    }

    const ok = await dispatch(
      updateFacility(
        locationId as number,
        payload,
        isFacilityAdmin ? false : true,
        isFacilityAdmin ? undefined : 'LOCATION_INFO',
      ) as any,
    );
    if (ok) {
      toast.success('Location details updated');
      setIsEditing(false);
    }
  };

  const facilityInitialValues = useMemo(
    () => ({
      address1: facility?.parent_facility_address1 || '',
      address2: facility?.parent_facility_address2 || '',
      city: facility?.parent_facility_city || '',
      state: facility?.parent_facility_state || '',
      zip_code: facility?.parent_facility_zip_code || '',
      phone: facility?.parent_facility_phone || '',
    }),
    [facility],
  );

  const facilityValidationSchema = Yup.object({
    address1: requiredIfEdited(
      facilityInitialValues.address1,
      'Address is required',
    ),
    address2: Yup.string(),
    ...addressFieldValidators(facilityInitialValues),
  });

  const handleFacilitySubmit = async (values: typeof facilityInitialValues) => {
    const initial = facilityInitialValues;
    const payload: UpdateParentFacility = {};
    if (values.address1 !== initial.address1)
      payload.address1 = values.address1;
    if (values.address2 !== initial.address2)
      payload.address2 = values.address2;
    if (values.city !== initial.city) payload.city = values.city;
    if (values.state !== initial.state) payload.state = values.state;
    if (values.zip_code !== initial.zip_code)
      payload.zip_code = values.zip_code;
    if (values.phone !== initial.phone) payload.phone = values.phone;

    if (Object.keys(payload).length === 0) {
      setIsEditing(false);
      return;
    }

    if (!facility?.parent_facility_id) return;
    const ok = await dispatch(
      updateParentFacility(facility.parent_facility_id, payload) as any,
    );
    if (ok) {
      toast.success('Facility details updated');
      setIsEditing(false);
    }
  };

  if (isLocation) {
    if (isEditing) {
      return (
        <Formik
          initialValues={locationInitialValues}
          validationSchema={locationValidationSchema}
          onSubmit={handleLocationSubmit}
          validateOnChange={validateOnChange}
          validateOnBlur={validateOnChange}
          enableReinitialize
        >
          {({ dirty, isValid, isSubmitting, submitCount }) => {
            if (submitCount > 0 && !validateOnChange) setValidateOnChange(true);
            return (
              <Form>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={2}
                >
                  <Box
                    sx={contentColumnSx}
                    display="flex"
                    flexDirection="column"
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        color: lightBlack,
                        mb: 1,
                      }}
                    >
                      Medication window
                    </Typography>
                    <Box mb={3}>
                      <Field
                        name="med_window"
                        label=""
                        component={SelectFormField}
                        options={MEDICATION_WINDOWS.map((mw) => ({
                          label: mw,
                          value: mw,
                        }))}
                        useNewStyles
                        sx={selectFieldStyles}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        color: lightBlack,
                        mb: 1,
                      }}
                    >
                      Address
                    </Typography>
                    <Box mb={2}>
                      <Field
                        name="address"
                        placeholder="Address"
                        component={TextInputFormField}
                        sx={textFieldStyles}
                      />
                    </Box>
                    <Box mb={2}>
                      <Field
                        name="city"
                        placeholder="City"
                        component={TextInputFormField}
                        sx={textFieldStyles}
                      />
                    </Box>
                    <Grid container spacing={2} mb={3}>
                      <Grid item xs={6}>
                        <Field
                          name="state"
                          label="State"
                          component={SelectFormField}
                          options={stateOptions}
                          useNewStyles
                          sx={selectFieldStyles}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          name="zip_code"
                          placeholder="Zip code"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                    </Grid>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        color: lightBlack,
                        mb: 1,
                      }}
                    >
                      Phone number
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          name="phone"
                          format={PHONE_FORMAT}
                          placeholder="Phone number"
                          component={MaskedInput}
                          sx={textFieldStyles}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box display="flex" gap={1.5}>
                    <Button
                      type="button"
                      variant="text"
                      color="secondary"
                      onClick={() => setIsEditing(false)}
                      sx={formActionButtonSx}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={!dirty || !isValid || isSubmitting}
                      sx={formActionButtonSx}
                    >
                      Save details
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      );
    }

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
      >
        <Box sx={contentColumnSx}>
          <InfoRow
            label="Medication window"
            value={medWindowReadLabel(location?.med_window_time_sec)}
          />
          <InfoRow
            label="Address"
            value={renderAddress(
              location?.address,
              undefined,
              location?.city,
              location?.state,
              location?.zip_code,
            )}
          />
          <InfoRow label="Phone number" value={location?.phone || '—'} />
          <InfoRow label="Hubs" value={location?.total_devices ?? 0} />
          <InfoRow label="Patients" value={location?.total_patients ?? 0} />
          <InfoRow
            label="Date created"
            value={formatDateIgnoreTz(location?.created_at, 'MMM d, yyyy')}
          />
        </Box>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<EditOutlinedIcon />}
          onClick={() => setIsEditing(true)}
          sx={formActionButtonSx}
        >
          Edit details
        </Button>
      </Box>
    );
  }

  if (isEditing) {
    return (
      <Formik
        initialValues={facilityInitialValues}
        validationSchema={facilityValidationSchema}
        onSubmit={handleFacilitySubmit}
        enableReinitialize
      >
        {() => (
          <Form>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={2}
            >
              <Box sx={contentColumnSx} display="flex" flexDirection="column">
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: lightBlack,
                    mb: 1,
                  }}
                >
                  Address
                </Typography>
                <Box mb={2}>
                  <Field
                    name="address1"
                    placeholder="Address"
                    component={TextInputFormField}
                    sx={textFieldStyles}
                  />
                </Box>
                <Box mb={2}>
                  <Field
                    name="address2"
                    placeholder="Address 2 (optional)"
                    component={TextInputFormField}
                    sx={textFieldStyles}
                  />
                </Box>
                <Box mb={2}>
                  <Field
                    name="city"
                    placeholder="City"
                    component={TextInputFormField}
                    sx={textFieldStyles}
                  />
                </Box>
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={6}>
                    <Field
                      name="state"
                      label="State"
                      component={SelectFormField}
                      options={stateOptions}
                      useNewStyles
                      sx={selectFieldStyles}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      name="zip_code"
                      placeholder="Zip code"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                    />
                  </Grid>
                </Grid>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: lightBlack,
                    mb: 1,
                  }}
                >
                  Phone number
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      name="phone"
                      format={PHONE_FORMAT}
                      placeholder="Phone number"
                      component={MaskedInput}
                      sx={textFieldStyles}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box display="flex" gap={1.5}>
                <Button
                  type="button"
                  variant="contained"
                  color="error"
                  data-testid="deactivate-facility-button"
                  onClick={onDeactivateFacility}
                >
                  Deactivate facility
                </Button>
                <Button
                  type="button"
                  variant="text"
                  color="secondary"
                  onClick={() => setIsEditing(false)}
                  sx={formActionButtonSx}
                >
                  Cancel
                </Button>
                {/* No PUT /parent_facility/:id endpoint exists yet, so Save is
                      disabled; wiring it up is handled in the OI-1583 follow-up. */}
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled
                  sx={formActionButtonSx}
                >
                  Save details
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={2}
    >
      <Box sx={contentColumnSx}>
        <InfoRow
          label="Address"
          value={renderAddress(
            facility?.parent_facility_address1,
            facility?.parent_facility_address2,
            facility?.parent_facility_city,
            facility?.parent_facility_state,
            facility?.parent_facility_zip_code,
          )}
        />
        <InfoRow
          label="Phone number"
          value={facility?.parent_facility_phone || '—'}
        />
        <InfoRow label="Locations" value={facility?.total_locations ?? 0} />
        <InfoRow label="Hubs" value={facility?.total_devices ?? 0} />
        <InfoRow label="Patients" value={facility?.total_patients ?? 0} />
        <InfoRow
          label="Date created"
          value={formatDateIgnoreTz(
            facility?.parent_facility_created_at,
            'MMM d, yyyy',
          )}
        />
      </Box>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<EditOutlinedIcon />}
        onClick={() => setIsEditing(true)}
        sx={formActionButtonSx}
      >
        Edit details
      </Button>
    </Box>
  );
};

export default DetailsTab;
